import React, { useRef, useState } from "react";
import GridLines from "@/components/site/GridLines";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/enquiry`;
const EMPTY = { fullName: "", mobile: "", email: "", company: "", message: "" };

export default function Contact() {
  const [wa, setWa] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // {type:'ok'|'error', text}
  const inFlight = useRef(false); // synchronous guard against double clicks

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const validate = () => {
    const digits = values.mobile.replace(/\D/g, "");
    if (values.fullName.trim().length < 2) return "Please enter your full name.";
    if (digits.length < 8 || digits.length > 15) return "Please enter a valid mobile number.";
    if (!/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(values.email.trim()))
      return "Please enter a valid email address.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current || sending) return;
    const problem = validate();
    if (problem) {
      setStatus({ type: "error", text: problem });
      return;
    }
    inFlight.current = true;
    setSending(true);
    setStatus(null);
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: values.fullName.trim(),
          mobile: values.mobile.trim(),
          whatsapp: wa,
          email: values.email.trim(),
          company: values.company.trim(),
          message: values.message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error("request-failed");
        err.friendly =
          typeof data.detail === "string" && data.detail
            ? data.detail
            : "We could not submit your enquiry right now. Please try again.";
        throw err;
      }
      setValues(EMPTY);
      setWa(false);
      setStatus({ type: "ok", text: "Thank you. Your enquiry has been received." });
    } catch (err) {
      setStatus({
        type: "error",
        text: err.friendly || "We could not submit your enquiry right now. Please try again.",
      });
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  return (
    <section className="rf-contact" id="enquiry" data-testid="contact">
      <GridLines />
      <div className="rf-contact__rule" />
      <div className="rf-wrap">
        <div className="rf-contact__grid">
          <div className="rf-contact__left">
            <div className="rf-contact__copy">
              <h2 className="rf-h2">
                Planning a project? Get it well done with{" "}
                <br className="rf-br-d" />
                Renaflex.
              </h2>
              <p className="rf-body rf-contact__sub">
                Find the right Renaflex board for your application, specification or project
                requirement.
              </p>
              <div className="rf-contact__btns">
                <a href="#enquiry" className="rf-btn rf-btn--green rf-btn--quote" data-testid="cta-quote">
                  GET A QUOTE
                </a>
                <a href="#enquiry" className="rf-btn rf-btn--white rf-btn--dealer" data-testid="cta-dealer">
                  BECOME A DEALER
                </a>
              </div>
            </div>
            <div className="rf-contact__leftRule" />
          </div>

          <div className="rf-contact__right">
            <form className="rf-form" data-testid="enquiry-form" onSubmit={onSubmit} noValidate>
              <div className="rf-field">
                <input
                  type="text"
                  name="fullname"
                  placeholder="Full name*"
                  value={values.fullName}
                  onChange={set("fullName")}
                  data-testid="field-fullname"
                />
              </div>
              <div className="rf-field">
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Mobile*"
                  value={values.mobile}
                  onChange={set("mobile")}
                  data-testid="field-mobile"
                />
              </div>
              <label className="rf-check" data-testid="field-whatsapp">
                <input type="checkbox" checked={wa} onChange={(e) => setWa(e.target.checked)} />
                <span className="rf-check__box" aria-hidden="true" />
                <span className="rf-check__label">Do you have whatsapp activated on this number?</span>
              </label>
              <div className="rf-field">
                <input
                  type="email"
                  name="email"
                  placeholder="Email*"
                  value={values.email}
                  onChange={set("email")}
                  data-testid="field-email"
                />
              </div>
              <div className="rf-field">
                <input
                  type="text"
                  name="company"
                  placeholder="Company / Orgnisation"
                  value={values.company}
                  onChange={set("company")}
                  data-testid="field-company"
                />
              </div>
              <div className="rf-field rf-field--area">
                <textarea
                  name="message"
                  placeholder="Message"
                  rows={3}
                  value={values.message}
                  onChange={set("message")}
                  data-testid="field-message"
                />
              </div>
              <p className="rf-form__terms">
                By submitting this form you agree to the Terms and Conditions and Privacy Policy.
              </p>
              <button
                type="submit"
                className="rf-btn rf-btn--green rf-btn--submit"
                disabled={sending}
                data-testid="form-submit"
              >
                {sending ? "SENDING..." : "SUBMIT"}
              </button>
              {status ? (
                <p
                  className={`rf-form__status rf-form__status--${status.type}`}
                  role="status"
                  aria-live="polite"
                  data-testid={status.type === "ok" ? "form-success" : "form-error"}
                >
                  {status.text}
                </p>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
